import { Alert, Box, Button, Input } from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoader } from 'thermia-react-container';
import useApiClient from '../../api/api-client';
import { Device, LockedError } from '../../api/api-types';
import ErrorComponent from '../../components/error-component';
import InlineLoader from '../../components/inline-loader';

const DeviceVerificationComponent: FC<{ device: Device; onVerified: () => void }> = ({ device, onVerified }) => {
  const { Loader } = useLoader();
  const { showDeviceAccess, verifyDeviceAccess } = useApiClient();
  const { t } = useTranslation('portal');

  const [isDisplayLoading, setIsDisplayLoading] = useState(false);
  const [displayState, setDisplayState] = useState<{ kind: 'codeDisplayed' } | { kind: 'error'; message: string } | null>(null);
  const [isVerifyLoading, setIsVerifyLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  const onDisplayClick = async () => {
    try {
      setIsDisplayLoading(true);
      setDisplayState(null);
      await showDeviceAccess(device.installationId);
      setDisplayState({ kind: 'codeDisplayed' });
    } catch (error) {
      error instanceof LockedError
        ? setDisplayState({ kind: 'error', message: t('portal.keys.device-verification-component.error.locked') })
        : setDisplayState({ kind: 'error', message: t('portal.keys.device-verification-component.error.displayCode') });
    } finally {
      setIsDisplayLoading(false);
    }
  };

  const onVerifyClick = async () => {
    if (verificationCode.trim()) {
      try {
        setIsVerifyLoading(true);
        setDisplayState(null);
        const success = await verifyDeviceAccess(device.installationId, verificationCode);
        if (success) {
          setVerificationCode('');
          onVerified();
        } else {
          setDisplayState({ kind: 'error', message: t('portal.keys.device-verification-component.error.verifyCodeInvalid') });
        }
      } catch (error) {
        error instanceof LockedError
          ? setDisplayState({ kind: 'error', message: t('portal.keys.device-verification-component.error.locked') })
          : setDisplayState({ kind: 'error', message: t('portal.keys.device-verification-component.error.verifyCode') });
      } finally {
        setIsVerifyLoading(false);
      }
    }
  };

  return (
    <>
      <p>{t('portal.keys.device-verification-component.title')}</p>
      <Button variant="contained" color="primary" onClick={onDisplayClick} disabled={isDisplayLoading || isVerifyLoading}>
        {t('portal.keys.device-verification-component.displayCode')}
      </Button>
      {isDisplayLoading && <InlineLoader loader={Loader} />}
      <Box sx={{ minHeight: 100, display: 'flex', alignItems: 'center' }}>
        {displayState?.kind === 'codeDisplayed' && <Alert severity="info">{t('portal.keys.device-verification-component.codeDisplayed')}</Alert>}
        {displayState?.kind === 'error' && <ErrorComponent text={displayState.message} />}
      </Box>
      <Box>
        <Input
          placeholder={t('portal.keys.device-verification-component.codePlaceholder')}
          value={verificationCode}
          onChange={(e) => setVerificationCode(e.target.value)}
        />
        <Button variant="contained" color="primary" sx={{ marginLeft: 2 }} onClick={onVerifyClick} disabled={isVerifyLoading || isDisplayLoading}>
          {t('portal.keys.device-verification-component.verifyCode')}
        </Button>
        {isVerifyLoading && <InlineLoader loader={Loader} />}
      </Box>
    </>
  );
};

export default DeviceVerificationComponent;
